import { Link } from "@tanstack/react-router";
import { AdminShell } from "@project/admin/components/admin-shell";
import { StatCard, SectionCard } from "@project/admin/components/module-ui";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import {
  Users,
  HeartPulse,
  Phone,
  Activity,
  ArrowRight,
  ShieldAlert,
} from "lucide-react";
import {
  mockResidents,
  mockResidentActivities,
} from "@project/lib/mock-modules";

export default function ElderCareDashboard() {
  const total = mockResidents.length;
  const active = mockResidents.filter(r => r.status === "Active").length;
  const stable = mockResidents.filter(r => r.health === "Stable").length;
  const monitoring = mockResidents.filter(
    r => r.health === "Monitoring"
  ).length;
  const critical = mockResidents.filter(r => r.health === "Critical").length;

  return (
    <AdminShell
      title="Elder Care"
      subtitle="Overview of residents, health and daily care"
      actions={
        <Button asChild size="sm" variant="outline">
          <Link to="/admin/elder-care/residents">
            Residents Directory <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Link>
        </Button>
      }
    >
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total Residents"
          value={total}
          icon={<Users className="h-5 w-5" />}
          accent="from-primary/90 to-primary"
          sub="Across blocks A, B and C"
        />
        <StatCard
          label="Active Residents"
          value={active}
          icon={<Activity className="h-5 w-5" />}
          accent="from-emerald-600 to-emerald-500"
          sub={`${total - active} on leave / hospitalised`}
        />
        <StatCard
          label="Health: Stable"
          value={`${stable} / ${total}`}
          icon={<HeartPulse className="h-5 w-5" />}
          accent="from-amber-600 to-amber-500"
          sub={`${monitoring} monitoring · ${critical} critical`}
        />
        <StatCard
          label="Critical Cases"
          value={critical}
          icon={<ShieldAlert className="h-5 w-5" />}
          accent="from-rose-600 to-rose-500"
          sub="Requires immediate attention"
        />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <SectionCard
          title="Health Status Summary"
          subtitle="Distribution across residents"
          className="lg:col-span-2"
        >
          <div className="space-y-3">
            {[
              { label: "Stable", value: stable, color: "bg-emerald-500" },
              { label: "Monitoring", value: monitoring, color: "bg-amber-500" },
              { label: "Critical", value: critical, color: "bg-rose-500" },
            ].map(row => {
              const pct = total ? Math.round((row.value / total) * 100) : 0;
              return (
                <div key={row.label}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="font-medium text-foreground">
                      {row.label}
                    </span>
                    <span className="text-muted-foreground">
                      {row.value} ({pct}%)
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full ${row.color}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>

        <SectionCard
          title="Emergency Contacts"
          subtitle="Primary family contacts on file"
        >
          <div className="divide-y divide-border">
            {mockResidents.slice(0, 5).map(r => (
              <div
                key={r.id}
                className="flex items-center justify-between gap-3 py-2.5"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-foreground">
                    {r.emergencyContact.name}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {r.emergencyContact.relation} of {r.name}
                  </div>
                </div>
                <a
                  href={`tel:${r.emergencyContact.mobile.replace(/\s/g, "")}`}
                  className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  <Phone className="h-3.5 w-3.5" /> Call
                </a>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <SectionCard
          title="Recent Activities"
          subtitle="Live feed across the home"
          className="lg:col-span-2"
        >
          <ul className="space-y-3">
            {mockResidentActivities.map(a => (
              <li key={a.id} className="flex items-start gap-3">
                <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />
                <div className="min-w-0">
                  <div className="text-sm text-foreground">{a.text}</div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    {a.when}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard title="Quick Status" subtitle="Snapshot by block">
          <div className="grid grid-cols-3 gap-2">
            {["A", "B", "C"].map(block => {
              const inBlock = mockResidents.filter(r =>
                r.room.startsWith(block)
              );
              return (
                <div
                  key={block}
                  className="bg-muted/40 rounded-lg border border-border p-3 text-center"
                >
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Block
                  </div>
                  <div className="text-lg font-bold text-foreground">
                    {block}
                  </div>
                  <Badge variant="secondary" className="mt-1">
                    {inBlock.length} residents
                  </Badge>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>
    </AdminShell>
  );
}
